import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { formatDate } from "@/i18n/format";
import type { ModeratorProductFeedback, ModeratorUnverifiedHsCodeProduct } from "@/services/moderators/generated/model";
import {
  getModeratorsProductFeedbackListQueryKey,
  useModeratorsProductFeedbackApproveCreate,
  useModeratorsProductFeedbackCommentPartialUpdate,
  useModeratorsProductFeedbackList,
  useModeratorsProductFeedbackRejectCreate,
  useModeratorsProductFeedbackUnverifiedHsCodesRetrieve,
} from "@/services/moderators/generated/moderators-product-feedback/moderators-product-feedback";
import "./ModeratorPage.css";
import "./ModeratorProductFeedbackPage.css";

const STATUS_OPTIONS = ["", "pending", "approved", "rejected"] as const;

const ModeratorProductFeedbackPage = () => (
  <ModeratorLayout>
    <FeedbackContent />
  </ModeratorLayout>
);

/** OQ-05: products saved with an HS code that isn't in the HS table. */
const UnverifiedHsCodes = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const { data, isLoading, error } = useModeratorsProductFeedbackUnverifiedHsCodesRetrieve();
  const products = unwrapPaginated<ModeratorUnverifiedHsCodeProduct>(data);

  if (isLoading || (!error && products.length === 0)) return null;
  return (
    <section className="mod-page__card">
      <div className="mod-page__card-header">
        <h2 className="mod-page__card-title">{t("productFeedback.unverified.title")}</h2>
        <span className="mod-page__card-meta">{t("productFeedback.unverified.subtitle")}</span>
      </div>
      <div className="mod-page__card-body">
        {error ? <div className="mod-page__error">{t("productFeedback.unverified.loadError")}</div> : null}
        {products.map((p) => (
          <div key={p.id} className="mod-page__form-row">
            <span className="mod-page__chip mod-page__chip--warning">{t("productFeedback.unverified.chip")}</span>
            <strong>{p.hsCode}</strong>
            <span>{p.name}</span>
            <span className="mod-page__card-meta">{p.companyName}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const FeedbackContent = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number]>("pending");
  const [openId, setOpenId] = useState<number | null>(null);

  const queryParams = useMemo(() => (statusFilter ? { status: statusFilter } : undefined), [statusFilter]);
  const { data, isLoading, error } = useModeratorsProductFeedbackList(queryParams);
  const feedbacks = unwrapPaginated<ModeratorProductFeedback>(data);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getModeratorsProductFeedbackListQueryKey(queryParams) });

  const approveMutation = useModeratorsProductFeedbackApproveCreate({ mutation: { onSuccess: invalidate } });
  const rejectMutation = useModeratorsProductFeedbackRejectCreate({ mutation: { onSuccess: invalidate } });
  const commentMutation = useModeratorsProductFeedbackCommentPartialUpdate({ mutation: { onSuccess: invalidate } });

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("productFeedback.title")}</h1>
          <p className="mod-page__subtitle">{t("productFeedback.subtitle")}</p>
        </div>
      </header>

      <div className="mod-page__toolbar">
        <select
          className="mod-page__select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as (typeof STATUS_OPTIONS)[number])}
        >
          <option value="">{t("common:labels.all")}</option>
          <option value="pending">{t("shared.pending")}</option>
          <option value="approved">{t("shared.approved")}</option>
          <option value="rejected">{t("shared.rejected")}</option>
        </select>
      </div>

      <UnverifiedHsCodes />

      {isLoading ? <div className="mod-page__loading">{t("productFeedback.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("productFeedback.loadError")}</div> : null}
      {!isLoading && feedbacks.length === 0 ? (
        <div className="mod-page__empty">{t("productFeedback.empty")}</div>
      ) : null}

      {feedbacks.map((fb) => (
        <FeedbackRow
          key={fb.id}
          fb={fb}
          isOpen={openId === fb.id}
          onToggle={() => setOpenId(openId === fb.id ? null : fb.id)}
          onApprove={(payload) => approveMutation.mutate({ id: fb.id, data: payload })}
          onReject={() => rejectMutation.mutate({ id: fb.id })}
          onComment={(text) => commentMutation.mutate({ id: fb.id, data: { moderatorComment: text } })}
          isApproving={approveMutation.isPending && approveMutation.variables?.id === fb.id}
          isRejecting={rejectMutation.isPending && rejectMutation.variables?.id === fb.id}
          isCommenting={commentMutation.isPending && commentMutation.variables?.id === fb.id}
        />
      ))}
    </div>
  );
};

interface FeedbackRowProps {
  fb: ModeratorProductFeedback;
  isOpen: boolean;
  onToggle: () => void;
  onApprove: (payload: { correctedProductName: string; correctedHscode: string }) => void;
  onReject: () => void;
  onComment: (text: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
  isCommenting: boolean;
}

const FeedbackRow = ({
  fb,
  isOpen,
  onToggle,
  onApprove,
  onReject,
  onComment,
  isApproving,
  isRejecting,
  isCommenting,
}: FeedbackRowProps) => {
  const { t } = useTranslation(["moderator", "common"]);
  const [correctedName, setCorrectedName] = useState(fb.correctedProductName || fb.productName || "");
  const [correctedHs, setCorrectedHs] = useState(fb.correctedHscode || fb.hscode || "");
  const [comment, setComment] = useState(fb.moderatorComment || "");

  const isPending = fb.status === "pending";

  return (
    <article className="mod-page__card mod-feedback__card">
      <div className="mod-page__card-body">
        <h2 className="mod-page__card-title">
          {t("productFeedback.feedbackId", { id: fb.id })} ·{" "}
          <span className={`mod-page__chip ${chipClass(fb.status)}`}>{fb.status}</span>
        </h2>
        <p className="mod-page__card-meta">
          {t("productFeedback.from", {
            email: fb.userEmail,
            company: fb.companyName || t("productFeedback.noCompany"),
            date: formatDate(fb.createdAt),
          })}
        </p>
        <p className="mod-page__card-meta">
          <Trans
            t={t}
            i18nKey="productFeedback.reported"
            values={{ product: fb.productName, hsCode: fb.hscode }}
            components={{ strong: <strong /> }}
          />
        </p>
        {fb.message ? (
          <p className="mod-page__card-meta">{t("productFeedback.userNote", { message: fb.message })}</p>
        ) : null}
        {fb.correctedProductName || fb.correctedHscode ? (
          <p className="mod-page__card-meta">
            <Trans
              t={t}
              i18nKey="productFeedback.approvedCorrection"
              values={{ product: fb.correctedProductName, hsCode: fb.correctedHscode }}
              components={{ strong: <strong /> }}
            />
          </p>
        ) : null}

        {isOpen ? (
          <div className="mod-feedback__detail">
            {isPending ? (
              <div className="mod-feedback__detail-block">
                <h3>{t("productFeedback.approveWithCorrection")}</h3>
                <div className="mod-page__form-row">
                  <input
                    type="text"
                    className="mod-page__input"
                    placeholder={t("productFeedback.correctedName")}
                    value={correctedName}
                    onChange={(e) => setCorrectedName(e.target.value)}
                  />
                  <input
                    type="text"
                    className="mod-page__input"
                    placeholder={t("productFeedback.correctedHs")}
                    value={correctedHs}
                    onChange={(e) => setCorrectedHs(e.target.value)}
                  />
                </div>
                <div className="mod-page__form-row">
                  <button
                    type="button"
                    className="mod-page__btn mod-page__btn--primary"
                    disabled={!correctedName.trim() || !correctedHs.trim() || isApproving}
                    onClick={() =>
                      onApprove({
                        correctedProductName: correctedName.trim(),
                        correctedHscode: correctedHs.trim(),
                      })
                    }
                  >
                    {isApproving ? t("companyDetail.approving") : t("common:actions.approve")}
                  </button>
                  <button
                    type="button"
                    className="mod-page__btn mod-page__btn--danger"
                    onClick={onReject}
                    disabled={isRejecting}
                  >
                    {isRejecting ? t("productFeedback.rejecting") : t("common:actions.reject")}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="mod-feedback__detail-block">
              <h3>{t("productFeedback.moderatorComment")}</h3>
              <textarea
                className="mod-page__textarea"
                placeholder={t("productFeedback.commentPlaceholder")}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="mod-page__form-row">
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--ghost"
                  onClick={() => onComment(comment)}
                  disabled={isCommenting}
                >
                  {isCommenting ? t("common:actions.saving") : t("productFeedback.saveComment")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="mod-feedback__actions">
        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={onToggle}>
          {isOpen ? t("common:actions.close") : t("productFeedback.review")}
        </button>
      </div>
    </article>
  );
};

function chipClass(status: string): string {
  if (status === "approved") return "mod-page__chip--success";
  if (status === "rejected") return "mod-page__chip--danger";
  return "mod-page__chip--warning";
}

export default ModeratorProductFeedbackPage;
